import { CoherenceTheme } from '@coherence-design-system/styles';
import React from 'react';
import {
  DocumentRegular,
  DocumentPdfRegular,
  DocumentTableRegular,
  MailRegular,
  TableAltTextRegular,
  DocumentImageRegular,
  DocumentTextRegular,
  DocumentBulletListRegular,
} from '@fluentui/react-icons';
import {
  makeStyles,
  Tooltip,
  createTableColumn,
  TableCellLayout,
  TableColumnDefinition,
} from '@fluentui/react-components';
import momentUtc from '../../../utils/DateFormatter';
import StatDateFormats from '../../../utils/types/DateFormats';
import getIconName from '../formFields/fileUpload/fileSelector.config';
import { openBlob } from './documentDownload';
import ICompanyMilestoneDocument from '../../../utils/types/ICompanyMilestoneDocument';

const useStyles = makeStyles({
  linkButton: {
    backgroundColor: 'transparent',
    border: 'none',
    cursor: 'pointer',
    margin: 0,
    padding: 0,
    textAlign: 'left',
    textDecoration: 'none',
    color: CoherenceTheme.palette.themePrimary,
    width: '100%',
    minWidth: 0,
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    whiteSpace: 'nowrap',
    maxWidth: '100%',
    display: 'block',
    '&:hover': {
      textDecoration: 'underline',
    },
  },
});

const renderTruncatedCellText = (content?: string): JSX.Element => {
  const text = content || '';
  const textElement = <span>{text}</span>;

  if (!text) {
    return textElement;
  }

  return (
    <Tooltip withArrow content={text} relationship="label">
      {textElement}
    </Tooltip>
  );
};

const getDocumentListColumns = (
  onError: (errorMessage?: string) => void,
): TableColumnDefinition<ICompanyMilestoneDocument>[] => {
  const styles = useStyles();

  return [
    createTableColumn<ICompanyMilestoneDocument>({
      columnId: 'file',
      renderHeaderCell: () => {
        return <DocumentRegular />;
      },
      renderCell: (item) => {
        let icon;
        const type = getIconName(item.title);
        switch (type) {
          case 'WordLogo':
            icon = <DocumentBulletListRegular />;
            break;
          case 'TextDocument':
            icon = <DocumentTextRegular />;
            break;
          case 'PowerPointLogo':
            icon = <TableAltTextRegular />;
            break;
          case 'PDF':
            icon = <DocumentPdfRegular />;
            break;
          case 'ExcelLogo':
            icon = <DocumentTableRegular />;
            break;
          case 'OutlookLogo':
            icon = <MailRegular />;
            break;
          case 'FileImage':
            icon = <DocumentImageRegular />;
            break;
          default:
            icon = <DocumentRegular />;
        }
        return <TableCellLayout>{icon}</TableCellLayout>;
      },
    }),
    createTableColumn<ICompanyMilestoneDocument>({
      columnId: 'title',
      renderHeaderCell: () => {
        return 'Title';
      },
      renderCell: (item) => {
        return (
          <Tooltip withArrow content={item.title} relationship="inaccessible">
            <button
              type="button"
              aria-label={`Open Document: ${item.title}`}
              onClick={async () => {
                return openBlob(item.documentId ?? 0, item.title, onError);
              }}
              className={styles.linkButton}
            >
              {item.title}
            </button>
          </Tooltip>
        );
      },
    }),
    createTableColumn<ICompanyMilestoneDocument>({
      columnId: 'dateUploadedValue',
      renderHeaderCell: () => {
        return 'Date Uploaded';
      },
      renderCell: (item) => {
        const uploadedDate = momentUtc(item.uploadDate, StatDateFormats.DayBreakout);
        return renderTruncatedCellText(uploadedDate);
      },
    }),
    createTableColumn<ICompanyMilestoneDocument>({
      columnId: 'uploadedBy',
      renderHeaderCell: () => {
        return 'Uploaded By';
      },
      renderCell: (item) => {
        const uploaderDisplayName = item.uploader?.displayName;
        return renderTruncatedCellText(uploaderDisplayName);
      },
    }),
    createTableColumn<ICompanyMilestoneDocument>({
      columnId: 'processType',
      renderHeaderCell: () => {
        return 'Process Type';
      },
      renderCell: (item) => {
        const { processType } = item;
        return renderTruncatedCellText(processType);
      },
    }),
    createTableColumn<ICompanyMilestoneDocument>({
      columnId: 'milestoneName',
      renderHeaderCell: () => {
        return 'Milestone Name';
      },
      renderCell: (item) => {
        const { milestoneName } = item;
        return renderTruncatedCellText(milestoneName);
      },
    }),
    createTableColumn<ICompanyMilestoneDocument>({
      columnId: 'processName',
      renderHeaderCell: () => {
        return 'Process Name';
      },
      renderCell: (item) => {
        const { processName } = item;
        return renderTruncatedCellText(processName);
      },
    }),
  ];
};

export default getDocumentListColumns;
